import EmptyState from '../util/EmptyState';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { t } from 'i18next';
import CourseViewer from './CourseViewer';
import CourseBuilder from './CourseBuilder';
import PageToolbar from '../util/PageToolbar';
import SearchFilter from '../util/SearchFilter';
import TabMenu from '../util/TabMenu';
import { format, addDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import Tooltip from '@mui/material/Tooltip';

// ── Types ────────────────────────────────────────────────────────────────────

export interface TrainingCourse {
  id: number;
  title: string;
  description?: string | null;
  isActive?: boolean;
  passThreshold: number;
  pageCount: number;
  questionCount: number;
  isForAll: boolean;
  isMandatory?: boolean;
  retakeIntervalDays?: number | null;
  publishAt?: string | null;
  departments?: { id: number; title: string }[];
  lastResult?: { score: number; completedAt: string; attempt: number } | null;
  assignedCount?: number;
  passedCount?: number;
  failedCount?: number;
  userResults?: {
    userId: number;
    userName: string;
    score: number | null;
    completedAt: string | null;
    status: 'passed' | 'failed' | 'not_started';
  }[];
}

export const scoreColor = (score: number) =>
  score >= 80 ? '#059669' : score >= 50 ? '#d97706' : '#dc2626';

// ── Helpers ───────────────────────────────────────────────────────────────────

const COURSE_COLORS: { keys: string[]; top: string; icon: string; emoji: string; solid: string }[] = [
  { keys: ['gdpr','data','security','password','protect'], top: 'linear-gradient(90deg,#7c3aed,#a78bfa)', icon: 'rgba(124,58,237,.12)', emoji: '🔒', solid: '#7c3aed' },
  { keys: ['fire'],                                        top: 'linear-gradient(90deg,#dc2626,#f87171)', icon: 'rgba(220,38,38,.1)',  emoji: '🧯', solid: '#dc2626' },
  { keys: ['health','medical','first aid'],                top: 'linear-gradient(90deg,#059669,#34d399)', icon: 'rgba(5,150,105,.12)', emoji: '🏥', solid: '#059669' },
  { keys: ['it','cyber','phish'],                          top: 'linear-gradient(90deg,#0891b2,#06b6d4)', icon: 'rgba(8,145,178,.12)', emoji: '💻', solid: '#0891b2' },
  { keys: ['equal','diversity'],                           top: 'linear-gradient(90deg,#db2777,#f472b6)', icon: 'rgba(219,39,119,.1)', emoji: '🤝', solid: '#db2777' },
  { keys: ['mental','wellbeing'],                          top: 'linear-gradient(90deg,#d97706,#fbbf24)', icon: 'rgba(217,119,6,.12)', emoji: '🧠', solid: '#d97706' },
  { keys: ['lead','manager'],                              top: 'linear-gradient(90deg,#2563eb,#60a5fa)', icon: 'rgba(37,99,235,.12)', emoji: '🚀', solid: '#2563eb' },
  { keys: ['finance','account','budget'],                  top: 'linear-gradient(90deg,#059669,#34d399)', icon: 'rgba(5,150,105,.12)', emoji: '💰', solid: '#059669' },
  { keys: ['hr','people','staff','onboard'],               top: 'linear-gradient(90deg,#db2777,#f472b6)', icon: 'rgba(219,39,119,.1)', emoji: '👥', solid: '#db2777' },
  { keys: ['safety'],                                      top: 'linear-gradient(90deg,#d97706,#fbbf24)', icon: 'rgba(217,119,6,.12)', emoji: '⚠️', solid: '#d97706' },
];

function getCourseStyle(title: string) {
  const lower = title.toLowerCase();
  for (const c of COURSE_COLORS) {
    if (c.keys.some(k => lower.includes(k)))
      return { top: c.top, icon: c.icon, emoji: c.emoji, solid: c.solid };
  }
  return { top: 'linear-gradient(90deg,#059669,#0d9488)', icon: 'rgba(5,150,105,.12)', emoji: '📚', solid: '#059669' };
}

function fmtRetake(days: number): string {
  if (days >= 365) return `${Math.round(days / 365)}y`;
  if (days >= 30)  return `${Math.floor(days / 30)}mo`;
  return `${days}d`;
}

function getPct(course: TrainingCourse): number {
  if (!course.lastResult) return 0;
  const score = course.lastResult.score ?? 0;
  return score >= (course.passThreshold ?? 80) ? 100 : Math.round(score);
}

// Backend sets lastResult=null when retake interval exceeded, so 'overdue' appears as 'not_started'
type Status = 'completed' | 'in_progress' | 'not_started';

function getStatus(course: TrainingCourse): Status {
  const pct = getPct(course);
  if (pct >= 100) return 'completed';
  if (pct > 0)    return 'in_progress';
  return 'not_started';
}

// ── CourseCard ────────────────────────────────────────────────────────────────

interface CourseCardProps {
  course: TrainingCourse;
  onOpen: (course: TrainingCourse) => void;
  showStats?: boolean;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, onOpen, showStats }) => {
  const { top, icon, emoji, solid } = getCourseStyle(course.title);
  const pct    = getPct(course);
  const status = getStatus(course);
  const isNew  = !course.lastResult;

  const btnClass = status === 'completed' ? 'tr-btn-retake'
    : pct > 0                             ? 'tr-btn-continue'
    :                                       'tr-btn-start';
  const btnLabel = status === 'completed' ? 'Retake'
    : pct > 0                             ? 'Continue →'
    :                                       'Start';

  return (
    <div className="tr-course-card" onClick={() => onOpen(course)}>
      {isNew && <span className="tr-new-badge">NEW</span>}
      <div className="tr-course-top-bar" style={{ background: top }} />
      <div className="tr-course-body">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div className="tr-course-icon-wrap" style={{ background: icon }}>{emoji}</div>
          <div>
            <div className="tr-course-title">{course.title}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span className="tr-course-cat-badge" style={{ background: icon, color: solid }}>
                {course.isForAll ? 'All staff' : 'Assigned'}
              </span>
              {course.isMandatory
                ? <span className="tr-course-mandatory">Mandatory</span>
                : <span className="tr-course-optional">Optional</span>}
            </div>
          </div>
        </div>
        {course.description && (
          <div className="tr-course-desc">{course.description}</div>
        )}
        <div className="tr-course-time">
          ⏱ {course.pageCount} pages · {course.questionCount} quiz{course.questionCount !== 1 ? 'zes' : ''}
          {course.retakeIntervalDays ? ` · Retake every ${fmtRetake(course.retakeIntervalDays)}` : ' · No retake required'}
        </div>
        {showStats && (course.assignedCount != null) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 8, borderTop: '1px solid var(--border-input)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>👥 {course.assignedCount} enrolled</span>
            {course.passedCount != null && (
              <>
                <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>·</span>
                <span style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>
                  {course.assignedCount > 0 ? Math.round((course.passedCount / course.assignedCount) * 100) : 0}% pass rate
                </span>
              </>
            )}
          </div>
        )}
        {!showStats && (
          <div className="tr-course-prog-row">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{pct > 0 ? `${pct}% complete` : 'Not started'}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: pct >= 100 ? '#059669' : pct > 0 ? '#d97706' : 'var(--text-color-calendar-week)' }}>{pct}%</span>
            </div>
            <div style={{ height: 5, borderRadius: 3, background: 'var(--border-input)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: top, transition: 'width .6s' }} />
            </div>
          </div>
        )}
      </div>
      <div className="tr-course-footer">
        <div className="tr-course-due">
          {status === 'completed'
            ? '✓ Completed'
            : course.retakeIntervalDays
              ? `Retake every ${fmtRetake(course.retakeIntervalDays)}`
              : ''}
        </div>
        <button type="button" className={`tr-course-btn ${btnClass}`}
          onClick={e => { e.stopPropagation(); onOpen(course); }}>
          {btnLabel}
        </button>
      </div>
    </div>
  );
};

// ── Main Training component ───────────────────────────────────────────────────

interface TrainingProps {
  access: any;
  baseUrl: string;
}

type Tab = 'overview' | 'admin';

const Training: React.FC<TrainingProps> = ({ access, baseUrl }) => {
  const auth    = useSelector((state: RootState) => state.auth.value);
  const isAdmin = access?.training_admin === true || access?.training_admin === 1;
  const { enqueueSnackbar } = useSnackbar();

  const [tab,          setTab]          = useState<Tab>('overview');
  const [search,       setSearch]       = useState('');
  const [showKPI,      setShowKPI]      = useState(true);
  const [assigned,     setAssigned]     = useState<TrainingCourse[]>([]);
  const [allCourses,   setAllCourses]   = useState<TrainingCourse[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [viewerCourse, setViewerCourse] = useState<TrainingCourse | null>(null);
  const [showAdd,      setShowAdd]      = useState(false);
  const [editCourse,   setEditCourse]   = useState<TrainingCourse | null>(null);
  const [selectedAdminCourse, setSelectedAdminCourse] = useState<TrainingCourse | null>(null);
  const [deletingId,   setDeletingId]   = useState<number | null>(null);
  const [notifyingId,  setNotifyingId]  = useState<number | null>(null);
  const [remindingId,  setRemindingId]  = useState<number | null>(null);
  const [togglingId,   setTogglingId]   = useState<number | null>(null);

  const handleTogglePublish = useCallback(async (course: TrainingCourse) => {
    const next = !course.isActive;
    setTogglingId(course.id);
    try {
      await axios.put(`training/course/${course.id}`, { isActive: next });
      setAllCourses(prev => prev.map(c => c.id === course.id ? { ...c, isActive: next } : c));
      setSelectedAdminCourse(prev => prev && prev.id === course.id ? { ...prev, isActive: next } : prev);
      enqueueSnackbar(next ? (t('TrainingPublished') || 'Published') : (t('TrainingUnpublished') || 'Unpublished'), { variant: 'success' });
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    } finally {
      setTogglingId(null);
    }
  }, [enqueueSnackbar]);

  const handleDeleteCourse = useCallback(async (id: number) => {
    if (!window.confirm('Delete this course? All pages, questions and results will be permanently removed.')) return;
    setDeletingId(id);
    try {
      await axios.delete(`training/course/${id}`);
      setAllCourses(prev => prev.filter(c => c.id !== id));
      if (selectedAdminCourse?.id === id) setSelectedAdminCourse(null);
    } catch { /* ignore */ } finally {
      setDeletingId(null);
    }
  }, [selectedAdminCourse]);

  const handleNotify = useCallback(async (id: number) => {
    setNotifyingId(id);
    try {
      await axios.post(`training/course/${id}/notify`);
      enqueueSnackbar(t('TrainingNotifySent'), { variant: 'success' });
    } catch { enqueueSnackbar('Error', { variant: 'error' }); } finally {
      setNotifyingId(null);
    }
  }, [enqueueSnackbar]);

  const handleRemind = useCallback(async (id: number) => {
    setRemindingId(id);
    try {
      await axios.post(`training/course/${id}/remind`);
      enqueueSnackbar(t('TrainingReminderSent'), { variant: 'success' });
    } catch { enqueueSnackbar('Error', { variant: 'error' }); } finally {
      setRemindingId(null);
    }
  }, [enqueueSnackbar]);

  const load = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const [{ data: asgn }] = await Promise.all([
        axios.get('training/assigned'),
      ]);
      setAssigned(Array.isArray(asgn) ? asgn : []);

      if (isAdmin) {
        const { data: all } = await axios.get('training/course');
        setAllCourses(Array.isArray(all) ? all : []);
      }
    } catch {}
    setLoading(false);
  }, [auth, isAdmin]);

  useEffect(() => { if (auth) load(); }, [auth, load]);

  // Stats derived from assigned courses
  const stats = useMemo(() => {
    return assigned.reduce(
      (acc, c) => {
        const s = getStatus(c);
        acc.assigned++;
        if (s === 'completed')    acc.completed++;
        if (s === 'in_progress')  acc.inProgress++;
        if (s === 'not_started')  acc.notStarted++;
        return acc;
      },
      { assigned: 0, completed: 0, inProgress: 0, notStarted: 0 },
    );
  }, [assigned]);

  const filteredAssigned = useMemo(() => {
    if (!search.trim()) return assigned;
    const q = search.toLowerCase();
    return assigned.filter(c => c.title.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q));
  }, [assigned, search]);

  const inProgress   = useMemo(() => filteredAssigned.filter(c => getStatus(c) !== 'completed'), [filteredAssigned]);


  const openViewer = useCallback((course: TrainingCourse) => {
    setViewerCourse(course);
  }, []);

  // Result rows from assigned

  const handleAddSuccess = useCallback(() => {
    setShowAdd(false);
    setEditCourse(null);
    load();
  }, [load]);

  return (
    <div className="section-page">
    <TabMenu
      value={tab}
      onChange={(v: string) => setTab(v as Tab)}
      tabs={[
        { label: stats.inProgress > 0 ? `${t('TrainingMyLearning')} (${stats.inProgress})` : t('TrainingMyLearning'), value: 'overview' },
        { label: t('TrainingManage'), value: 'admin', hidden: !isAdmin },
      ]}
    >

      {/* ── PageToolbar ── */}
      <PageToolbar>
        <SearchFilter columns={[]} search={search} onSearch={setSearch} filters={[]} onFilters={() => {}} />
        <div style={{ flex: 1 }} />

        {tab === 'overview' && (
          <button type="button" onClick={() => setShowKPI(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px',
              borderRadius: 5, border: '1px solid var(--border-input)', cursor: 'pointer',
              fontSize: 11, fontWeight: 700,
              background: showKPI ? 'rgba(37,99,235,.1)' : 'var(--btn-user-form)',
              color: showKPI ? '#2563eb' : 'var(--text-color-calendar-week)',
              transition: 'all .15s',
            }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
            KPI
          </button>
        )}

        {isAdmin && (
          <button type="button" className="tr-btn-portal-primary"
            onClick={() => setShowAdd(true)}>
            + {t('TrainingCreateCourse')}
          </button>
        )}
      </PageToolbar>

      <div className="section-scroll">

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid var(--border-input)', borderTopColor: '#2563eb', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}

      {/* ── TAB: MY LEARNING ── */}
      {!loading && tab === 'overview' && (
        <div>
          {/* Stat cards (compact KPI row, like Reports) */}
          {showKPI && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, flexShrink: 0, marginBottom: 14 }}>
              {[
                { ic: '📚', n: stats.assigned,   l: 'Assigned',    color: '#2563eb', bg: 'rgba(37,99,235,.12)' },
                { ic: '✅', n: stats.completed,  l: 'Completed',   color: '#059669', bg: 'rgba(5,150,105,.12)' },
                { ic: '⏳', n: stats.inProgress, l: 'In Progress', color: '#d97706', bg: 'rgba(217,119,6,.12)' },
                { ic: '🕐', n: stats.notStarted, l: 'Not Started', color: '#64748b', bg: 'rgba(100,116,139,.12)' },
              ].map(s => (
                <div key={s.l} className="dashboard-card" style={{ padding: '8px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, background: s.bg, flexShrink: 0 }}>{s.ic}</div>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>{s.l}</div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-.02em', lineHeight: 1, marginTop: 6, color: s.color }}>{s.n}</div>
                </div>
              ))}
            </div>
          )}

          {/* Not started mandatory alert */}
          {inProgress.filter(c => c.isMandatory && getStatus(c) === 'not_started').map(c => (
            <div key={c.id} className="tr-alert-card">
              <div style={{ fontSize: 22 }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>{c.title} not yet started</div>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar)', marginTop: 2 }}>
                  Mandatory course · Please complete as soon as possible
                </div>
              </div>
              <button type="button" className="tr-course-btn tr-btn-start"
                onClick={() => openViewer(c)}>Start now</button>
            </div>
          ))}

          {/* All courses */}
          {filteredAssigned.length > 0 && (
            <div className="tr-course-grid">
              {filteredAssigned.map(c => <CourseCard key={c.id} course={c} onOpen={openViewer} />)}
            </div>
          )}

          {!loading && assigned.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: .5 }}>🎓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-color)', marginBottom: 6 }}>No courses assigned yet</div>
              <div style={{ fontSize: 13, color: 'var(--text-color-calendar-week)' }}>
                {isAdmin ? 'Create your first course to get started.' : 'Check back later for new courses.'}
              </div>
            </div>
          )}
        </div>
      )}



      {/* ── TAB: ADMIN ── */}
      {!loading && tab === 'admin' && isAdmin && (
        <div className="tr-admin-grid">
          {/* Course list */}
          <div>
            <div className="dt-card-wrap" style={{ overflow: 'hidden' }}>
              {allCourses.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>
                  No courses yet. Create your first course.
                </div>
              ) : allCourses.map(c => {
                const { emoji, icon, solid } = getCourseStyle(c.title);
                const pct = c.assignedCount && c.passedCount != null
                  ? Math.round((c.passedCount / c.assignedCount) * 100) : 0;
                const failedCount = (c.userResults ?? []).filter(r => r.status === 'failed').length;
                const isSelected = selectedAdminCourse?.id === c.id;
                return (
                  <div key={c.id}
                    className={`tr-admin-course-row${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelectedAdminCourse(isSelected ? null : c)}>
                    <div className="tr-admin-ic" style={{ background: icon }}>{emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>{c.title}</span>
                        {c.publishAt && new Date(c.publishAt) > new Date() && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'rgba(217,119,6,.1)', color: '#d97706', flexShrink: 0 }}>
                            {t('TrainingScheduled')}: {format(new Date(c.publishAt), 'dd MMM yyyy')}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                        {c.pageCount} pages · {c.questionCount} quizzes
                        {failedCount > 0 && <span style={{ color: '#dc2626', fontWeight: 700 }}> · {failedCount} failed</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <div className="tr-mini-bar">
                          <div className="tr-mini-fill" style={{ width: `${pct}%`, background: solid }} />
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{pct}% team completion</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                      <button type="button"
                        title={t('TrainingPublishToggleHint') as string}
                        disabled={togglingId === c.id}
                        style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 7, cursor: 'pointer',
                          border: `1px solid ${c.isActive ? 'rgba(5,150,105,.3)' : 'var(--border-input)'}`,
                          background: c.isActive ? 'rgba(5,150,105,.1)' : 'var(--btn-user-form)',
                          color: c.isActive ? '#059669' : 'var(--text-color-calendar-week)',
                          opacity: togglingId === c.id ? 0.5 : 1 }}
                        onClick={() => handleTogglePublish(c)}>
                        {togglingId === c.id ? '…' : (c.isActive ? `✓ ${t('TrainingPublished') || 'Published'}` : (t('TrainingDraft') || 'Draft'))}
                      </button>
                      <button type="button"
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color)', cursor: 'pointer' }}
                        onClick={() => setEditCourse(c)}>Edit</button>
                      <Tooltip title={t('TrainingNotifyUsers') as string} placement="top" arrow>
                        <span>
                          <button type="button"
                            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(37,99,235,.3)', background: 'rgba(37,99,235,.06)', color: '#2563eb', cursor: 'pointer', opacity: notifyingId === c.id ? 0.5 : 1 }}
                            disabled={notifyingId === c.id}
                            onClick={() => handleNotify(c.id)}>
                            {notifyingId === c.id ? '…' : '✉'}
                          </button>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('TrainingRemindUsers') as string} placement="top" arrow>
                        <span>
                          <button type="button"
                            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(217,119,6,.3)', background: 'rgba(217,119,6,.06)', color: '#d97706', cursor: 'pointer', opacity: remindingId === c.id ? 0.5 : 1 }}
                            disabled={remindingId === c.id}
                            onClick={() => handleRemind(c.id)}>
                            {remindingId === c.id ? '…' : '🔔'}
                          </button>
                        </span>
                      </Tooltip>
                      <button type="button"
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(220,38,38,.3)', background: 'rgba(220,38,38,.06)', color: '#dc2626', cursor: 'pointer', opacity: deletingId === c.id ? 0.5 : 1 }}
                        disabled={deletingId === c.id}
                        onClick={() => handleDeleteCourse(c.id)}>
                        {deletingId === c.id ? '…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-l1)' }}>
            <div className="dt-card-wrap" style={{ padding: 16 }}>
              {selectedAdminCourse ? (
                <>
                  <div className="tr-section-label" style={{ marginBottom: 14 }}>
                    <span className="tr-section-dot" style={{ background: '#2563eb' }} />
                    {selectedAdminCourse.title}
                  </div>
                  {(selectedAdminCourse.userResults ?? []).length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', textAlign: 'center', padding: '16px 0' }}>
                      No results yet
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(selectedAdminCourse.userResults ?? []).map(u => {
                        const initials = u.userName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                        const passed = u.status === 'passed';
                        const failed = u.status === 'failed';
                        const score  = u.score ?? 0;
                        return (
                          <div key={u.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border-input)' }}>
                            <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'white', background: 'linear-gradient(135deg,#1e40af,#60a5fa)' }}>
                              {initials}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.userName}</div>
                              {u.status !== 'not_started' && (
                                <div style={{ height: 4, borderRadius: 2, background: 'var(--border-input)', overflow: 'hidden', marginTop: 4 }}>
                                  <div style={{ height: '100%', borderRadius: 2, width: `${score}%`, background: passed ? '#059669' : failed ? '#dc2626' : '#d97706' }} />
                                </div>
                              )}
                            </div>
                            {u.status === 'not_started' ? (
                              <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>Not started</span>
                            ) : (
                              <>
                                <span style={{ fontSize: 12, fontWeight: 800, color: passed ? '#059669' : failed ? '#dc2626' : '#d97706', flexShrink: 0 }}>{Math.round(score)}%</span>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, flexShrink: 0,
                                  background: passed ? 'rgba(5,150,105,.1)' : 'rgba(220,38,38,.08)',
                                  color: passed ? '#059669' : '#dc2626' }}>
                                  {passed ? '✓ Pass' : '✗ Fail'}
                                </span>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="tr-section-label" style={{ marginBottom: 14 }}>
                    <span className="tr-section-dot" style={{ background: '#2563eb' }} />
                    Team Progress
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', textAlign: 'center', padding: '20px 0' }}>
                    Select a course to see results
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      </div>{/* /section-scroll */}

    </TabMenu>

      {/* Course Viewer */}
      {viewerCourse && (
        <CourseViewer
          course={viewerCourse}
          onClose={() => setViewerCourse(null)}
          onComplete={load}
        />
      )}

      {/* Course Builder (full-screen) */}
      {(showAdd || !!editCourse) && (
        <CourseBuilder
          course={editCourse}
          baseUrl={baseUrl}
          onClose={() => { setShowAdd(false); setEditCourse(null); }}
          onSaved={handleAddSuccess}
        />
      )}
    </div>
  );
};

export default Training;
